import React, { useEffect, useState } from 'react';
import { strings } from 'src/localization';
import classNames from 'classnames';
import * as css from './VehicleStatus.module.scss';
import { IconCheckMark } from 'src/content/icons';
import { RepairOrder } from 'src/models';
import { THEME } from 'src/constants/ThemeNames';

export interface IProps {
    repairOrder: RepairOrder;
    theme: string;
    locale: string;
    toggleROLangLits: (language: string) => void;
}

const VehicleStatus = (props: IProps) => {
    let currentStep = props.repairOrder.currentStatusIndex;
    currentStep = Number(currentStep);
    // Max out the statuses at 14 for now
    const dmsStatuses = props.repairOrder.dmsStatuses.slice(0, 14);

    useEffect(() => {
        props.toggleROLangLits(props.locale);
    }, [
        props.toggleROLangLits,
        props.locale,
    ]);

    // Set the selected step to whatever the current step is, if all steps are complete then currentStep
    // will equal -1. In that case set the selected step to the final step.
    let initialSelectedStep: number = currentStep;
    if (initialSelectedStep === -1) {
        initialSelectedStep = dmsStatuses.length - 1;
    }
    const [selectedStep, setSelectedStep] = useState(initialSelectedStep);
    // Set variable step css dependent on number of steps
    const stepClass = classNames(
        css.steps,
        {
            [css.steps1]: dmsStatuses.length === 1,
            [css.steps2]: dmsStatuses.length === 2,
            [css.steps3]: dmsStatuses.length === 3,
            [css.steps4]: dmsStatuses.length === 4,
            [css.steps5]: dmsStatuses.length === 5,
            [css.steps6]: dmsStatuses.length === 6,
            [css.steps7]: dmsStatuses.length === 7,
            [css.steps8]: dmsStatuses.length === 8,
            [css.steps9]: dmsStatuses.length === 9,
            [css.steps10]: dmsStatuses.length === 10,
            [css.steps11]: dmsStatuses.length === 11,
            [css.steps12]: dmsStatuses.length === 12,
            [css.steps13]: dmsStatuses.length === 13,
            [css.steps14]: dmsStatuses.length === 14,
        },
    );

    let vehicleStep: JSX.Element | null = null;
    if (currentStep !== -1) {
        vehicleStep = (<p>{strings.YourVehicleCurrentlyStep + (currentStep + 1) + strings.Of + dmsStatuses.length + ':'}</p>);
    } else {
        vehicleStep = (<p>{strings.YourVehicleCurrently + dmsStatuses[dmsStatuses.length-1].webTitle}</p>);
    }

    const steps = dmsStatuses.map((step, index) => {
        const completed = (index < currentStep) || (currentStep === -1);
        const current = index === currentStep;
        const final = (index === dmsStatuses.length - 1) && index !== 0;
        const flipStatus = (index > dmsStatuses.length * 2 / 3) && index !== 0;
        // Currently if there is only one status, center it and show the status bar before and after
        const only = (index === 0 && dmsStatuses.length === 1);
        // If selected include the selected class for styling
        // Double digit steps need a different class for the bullet
        const bulletClasses = classNames(
            index + 1 < 10 || completed ? css.bullet : css.bulletDoubleDigit,
            {
                [css.completed]: completed,
                [css.current]: current,
                [css.final]: final,
                [css.only]: only,
            },
        );
        const popupClasses = classNames(
            {
                [css.statusPopup]: !flipStatus,
                [css.statusPopupFlipped]: flipStatus,
                [css.only]: only,
            },
        );

        const titleClasses = classNames(
            css.title,
            {
                [css.titleHY]: props.theme === 'hyundai',
                [css.titleLM]: props.theme === 'lexusMonogram',
                [css.titleBMW]: props.theme === 'bmw',
                [css.titleFord]: props.theme === 'ford',
                [css.titleAudi]: props.theme === THEME.AUDI,


            },
        );

        const statusClasses = classNames(
            css.status,
            {
                [css.statusHY]: props.theme === 'hyundai',
                [css.statusLM]: props.theme === 'lexusMonogram',
                [css.statusAudi]: props.theme === THEME.AUDI,
            },
        );

        const checkClasses = classNames(
            css.check,
            {
                [css.checkAudi]: props.theme === THEME.AUDI,
            },
        );

        return (
            <div
                key={index}
                id={'step' + index}
                className={final ? css.final : stepClass}
                onClick={() => setSelectedStep(index)}
            >
                {index === selectedStep ?
                    <div className={popupClasses}>
                        <p className={titleClasses}>{step.webTitle}</p>
                        <p className={statusClasses}>
                            {strings.Status + ' ' + step.status}
                        </p>
                    </div> : null}
                <div className={bulletClasses}>
                    {completed ? <IconCheckMark className={checkClasses} /> : index + 1}
                </div>
            </div>
        );
    });

    const repairOrderNumStyles = classNames(
        css.repairOrderNum,
        {

            [css.repairOrderNumBMW]: props.theme === 'bmw',
            [css.repairOrderNumFord]: props.theme === 'ford',
            [css.repairOrderNumNissan]: props.theme === 'nissan',
        },
    );


    const vehicleStatusStyles = classNames(
        css.vehicleStatus,
        {
            [css.vehicleStatusBMW]: props.theme === 'bmw',
            [css.vehicleStatusFord]: props.theme === 'ford',
            [css.vehicleStatusIN]: props.theme === 'infiniti',
        },
    );

    return (
        <div>
            <p className={repairOrderNumStyles}>{strings.RepairOrderNum + props.repairOrder.RONum}</p>
            <p className={vehicleStatusStyles}>{strings.VehicleStatus}</p>
            {vehicleStep}
            <div className={css.stepProgressBar}>
                {steps}
            </div>
        </div>
    );
};


export default VehicleStatus;
